import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import CompactFilters, { ISelectOption } from '../../Components/Analyse/CompactFilters/CompactFilters';
import FilterSearchBar from '../../Components/SearchBar/FilterSearchBar/FilterSearchBar';
import PagedImproveCardCollection from '../../Components/Utils/LandingPages/Improve/PagedImproveCardCollection';
import ImproveExportWizard from '../../Components/Utils/LandingPages/ImproveExportWizard/ImproveExportWizard';
import Button from '../../Components/Utils/Button/Button';
import Tooltip from '../../Components/Utils/Tooltip/Tooltip';
import { GetImproveAreaDisplayInfosByClientAndUser, ToggleImproveAreaArchived } from '../../API/Improve/Improve';
import { AnalyseDataRequest } from '../../Types/Paging/PagedRequest';
import { useAppSelector } from '../../Redux/Store/Store';
import { getClient, getClientHasThirdPartySubsets, getClientRolePermission } from '../../Redux/Store/Slices/client';
import styles from './ImproveActive.module.css';
import { ImproveAreaDisplayInfo } from '../../Types/AreaDisplayInfo/AreaDisplayInfo';

// Mirrors ImproveActive so the archive filters read the same way.
const roleOptions: ISelectOption[] = [
  { label: 'Participant', value: 'Participant', required: false },
  { label: 'Reader', value: 'Reader', required: false },
  { label: 'Owner', value: 'Owner', required: false },
  { label: 'Lead', value: 'Lead', required: false },
];

const statusOptions: ISelectOption[] = [
  { label: 'Complete', value: 'Complete', required: false },
  { label: 'Overdue', value: 'Overdue', required: false },
  { label: 'In progress', value: 'In progress', required: false },
  { label: 'Not started', value: 'Not started', required: false },
];

const impactOptions: ISelectOption[] = [
  { label: 'Not Met', value: 'Not Met', required: false },
  { label: 'Partially Met', value: 'Partially Met', required: false },
  { label: 'Met', value: 'Met', required: false },
  { label: 'Exceeded', value: 'Exceeded', required: false },
];

type ImproveArchiveProps = {
  setShowImportTemplate: (show: boolean) => void;
};

const ImproveArchive = ({ setShowImportTemplate }: ImproveArchiveProps) => {

  const client = useAppSelector(getClient);
  const clientId = client?.id;
  const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);
  const clientRolePermissions = useAppSelector(getClientRolePermission);

  const [displayInfos, setDisplayInfos] = useState<ImproveAreaDisplayInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  const [exportPreselectedId, setExportPreselectedId] = useState<string | undefined>();

  const openBulkExport = (areaId?: string) => {
    setExportPreselectedId(areaId);
    setBulkExportOpen(true);
  };

  const closeBulkExport = () => {
    setBulkExportOpen(false);
    setExportPreselectedId(undefined);
  };

  const bulkSelectEnabled = useMemo(
    () => displayInfos.some((a) => a.canExport),
    [displayInfos]
  );

  // Filter state
  const [selectedOrganisations, setSelectedOrganisations] = useState<ISelectOption[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<ISelectOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<ISelectOption[]>([]);
  const [selectedTemplateTypes, setSelectedTemplateTypes] = useState<ISelectOption[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<ISelectOption[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<ISelectOption[]>([]);
  const [selectedImpact, setSelectedImpact] = useState<ISelectOption[]>([]);

  const filterConfig = useMemo(() => [
    { key: 'organisations', label: 'Organisations', placeholder: 'Select organisations', dataSource: 'api' as const },
    { key: 'departments', label: 'Departments', placeholder: 'Select departments', dataSource: 'api' as const },
    { key: 'groups', label: 'Groups', placeholder: 'Select groups', dataSource: 'api' as const },
    { key: 'templateTypes', label: 'Types', placeholder: 'Select types', dataSource: 'props' as const, data: [] },
    { key: 'roles', label: 'Roles', placeholder: 'Select your role', dataSource: 'props' as const, data: roleOptions },
    { key: 'status', label: 'Status', placeholder: 'Select status', dataSource: 'props' as const, data: statusOptions },
    { key: 'impact', label: 'Impact', placeholder: 'Select impact', dataSource: 'props' as const, data: impactOptions },
  ], []);

  const selectedFilters = useMemo(() => ({
    organisations: hasThirdPartySubsets ? selectedOrganisations : [],
    departments: hasThirdPartySubsets ? selectedDepartments : [],
    groups: selectedGroups,
    templateTypes: selectedTemplateTypes,
    roles: selectedRoles,
    status: selectedStatus,
    impact: selectedImpact,
  }), [hasThirdPartySubsets, selectedOrganisations, selectedDepartments, selectedGroups, selectedTemplateTypes, selectedRoles, selectedStatus, selectedImpact]);

  const handleFilterChange = (filters: Record<string, ISelectOption[]>) => {
    setSelectedOrganisations(filters.organisations || []);
    setSelectedDepartments(filters.departments || []);
    setSelectedGroups(filters.groups || []);
    setSelectedTemplateTypes(filters.templateTypes || []);
    setSelectedRoles(filters.roles || []);
    setSelectedStatus(filters.status || []);
    setSelectedImpact(filters.impact || []);
  };

  const fetchData = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const request: AnalyseDataRequest = {
        partnerIds: [],
        departmentIds: [],
        groupIds: [],
        role: '',
        archived: true,
        softFilter: true,
        pageNumber: 1,
        pageSize: 100000,
        searchTerm: '',
      };

      const response = await GetImproveAreaDisplayInfosByClientAndUser(clientId, request);
      setDisplayInfos(response.items || []);
    } catch (error) {
      console.error('Error fetching improve area display infos:', error);
      setDisplayInfos([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleArchive = async (areaId: string) => {
    try {
      await ToggleImproveAreaArchived(areaId);
      toast.success('Plan unarchived successfully');
      // Refresh the data after unarchiving
      await fetchData();
    } catch (error) {
      console.error('Error unarchiving improvement plan:', error);
      toast.error('Failed to unarchive plan');
    }
  };

  return (
    <div className={styles.container}>
      <ImproveExportWizard
        isVisible={bulkExportOpen}
        onClose={closeBulkExport}
        areas={displayInfos}
        archivedPlans={true}
        initialSelectedIds={exportPreselectedId ? [exportPreselectedId] : undefined}
      />
      <div className={styles.topContainer}>
        <FilterSearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          filters={filterConfig}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
        />
        <div className={styles.top}>
          <CompactFilters
            filters={filterConfig}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
          />

          <div className={styles.actionButtons}>
            {bulkSelectEnabled && (
              <Button
                colour="rgb(36, 57, 79)"
                textColour="#FFF"
                clickHandler={() => openBulkExport()}
              >
                Export data
              </Button>
            )}
            <Tooltip
              text={`Your role as a ${clientRolePermissions?.roleTitle} means you cannot import a template`}
              align="top"
              sideAlign="left"
              disabled={clientRolePermissions?.importTemplate}
            >
              <div className={styles.createButton}>
                <Button
                  disabled={!clientRolePermissions?.importTemplate}
                  colour="rgb(36, 57, 79)"
                  textColour="#FFF"
                  clickHandler={() => setShowImportTemplate(true)}
                >
                  Create new plan
                </Button>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>

      <PagedImproveCardCollection
        displayInfos={displayInfos}
        archived={true}
        toggleArchiveHandler={handleArchive}
        loading={loading}
        selectedRoleOption={selectedRoles}
        selectedPartners={selectedOrganisations}
        selectedDepartments={selectedDepartments}
        selectedGroups={selectedGroups}
        selectedStatus={selectedStatus}
        selectedImpact={selectedImpact}
        searchTerm={searchTerm}
        hideExportButton
        onCardExport={bulkSelectEnabled ? openBulkExport : undefined}
      />
    </div>
  );
};

export default ImproveArchive;
